import {AxisTitleOptions, AxisTypeValue, XAxisLabelsOptions, YAxisLabelsOptions} from 'highcharts';

import {PropertyType} from 'common/report/constants';
import {Behavior, CustomProperty, ProfileHistoryOptions, Segmentation} from 'common/types/reports/query';
import {BaseQueryErrorType, BaseQueryResultsType, ForecastValue} from 'common/report/types';
import {Bucket} from 'common/widgets/bucket-pane/types';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SerializedBehaviorDictionary} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {Expression} from 'common/query-builder/cohorts-query-builder/models/types/expression-types';
import type {BookmarkTheme} from 'common/report/queries/app-api/themes';
import {ListQuantifier} from 'common/widgets/property-filter-menu/models/types';
import {TranslationKey} from 'common/i18n';
import * as BookmarkTypes from 'common/types/reports/bookmark';

export type FilterDeterminer = `all` | `any`;

export interface FlatSegment<ValueT = number> {
  key: string;
  path: string[];
  displayName: string;
  displayNamePath?: string[];
  // object key is an ISO-8601 date string for time data
  // or a single arbitrary leaf key for series data e.g. `all`, `value`, `null`
  value: Record<string, ValueT>;
  secondaryValue?: Record<string, ValueT>;
}

export enum TypeOfComparison {
  Time = `time_comparison`,
  Lift = `lift_comparison`,
}

export const AnalysisTypeLabelKey: {[key in BookmarkTypes.AnalysisType]: TranslationKey} = {
  [BookmarkTypes.AnalysisType.Linear]: `reports.analysis-types.linear`,
  [BookmarkTypes.AnalysisType.Logarithmic]: `reports.analysis-types.logarithmic`,
  [BookmarkTypes.AnalysisType.Rolling]: `reports.analysis-types.rolling`,
  [BookmarkTypes.AnalysisType.Cumulative]: `reports.analysis-types.cumulative`,
};

export type AxisUnitOptions = {
  abbrev?: boolean;
  prefix?: string;
  suffix?: string;
  precision?: AxisUnitPrecision;
};

// Maximum precision is 8 decimal places
export type AxisUnitPrecision = BookmarkTypes.MetricDisplay[`precision`];
export const AXIS_UNIT_PRECISION_MIN = 0;
export const AXIS_UNIT_PRECISION_MAX = 8;

export type BookmarkYAxisOptions = {
  axisUnit?: AxisUnitOptions;
  labels?: Partial<YAxisLabelsOptions>;
  min?: number;
  max?: number;
  title?: Partial<AxisTitleOptions> & {placeholder?: string};
  type?: AxisTypeValue;
  useSoftMinMax?: boolean;
};

export type BookmarkXAxisOptions = {
  title?: Partial<AxisTitleOptions> & {placeholder?: string};
  labels?: Partial<XAxisLabelsOptions>;
};

/**
 * DisplayOptions is part of the shared bookmark schema, but the definitions
 * for some of the fields come from 3rd party JS libraries, so we generate the
 * GeneratedDisplayOptions type to allow any type for those fields and then override
 * them here to the correct type.
 *
 * If making updates to any field in DisplayOptions not overridden below, make the
 * change in the shared schema at lib/common/mxpnl/report/bookmarks/insights/display_options.py
 */
export interface DisplayOptions
  extends Omit<
    BookmarkTypes.DisplayOptions,
    `primaryYAxisOptions` | `secondaryYAxisOptions` | `theme` | `xAxisOptions`
  > {
  primaryYAxisOptions?: BookmarkYAxisOptions;
  secondaryYAxisOptions?: BookmarkYAxisOptions;
  theme?: BookmarkTheme;
  xAxisOptions?: BookmarkXAxisOptions;
}

/** Subset of SortByColumnsConfig:
 * 1) no valueField (used for lift in bar charts, I think)
 * 2) colSortAttrs can only be FlatLabelSortConfig, not FlatValueSortConfig */
export type OldTableSortByColumn = {
  sortBy: BookmarkTypes.SortBy.Column;
  colSortAttrs: Array<{sortBy: BookmarkTypes.SortBy.Label; sortOrder: BookmarkTypes.SortOrder}>;
};

/** Same as SortByValueConfig except:
 * 1) sortColumn (always 'sum') instead of valueField
 * 2) colSortAttrs can only be FlatLabelSortConfig, not FlatValueSortConfig
 *
 * Must convert this for use with data-table
 */
export type OldTableSortByValue = {
  sortBy: BookmarkTypes.SortBy.Value;
  sortOrder: BookmarkTypes.SortOrder;
  sortColumn: `sum`;
  colSortAttrs: Array<{sortBy: BookmarkTypes.SortBy.Label; sortOrder: BookmarkTypes.SortOrder}>;
};

export type OldTableSortConfig = OldTableSortByColumn | OldTableSortByValue;

export type InsightsSortConfig = BookmarkTypes.InsightsBookmarkSortConfig & {
  [BookmarkTypes.ChartType.Line]: BookmarkTypes.SortConfig;
};

export type MetricBehavior = BookmarkTypes.Behavior;

export type MetricMeasurementProperty = {
  dataset: string;
  name: string | string[];
  renamed?: string;
  custom?: boolean;
  id?: number;
  type: PropertyType;
  defaultType: PropertyType;
  // seems like sunset feature related
  is_collect_everything_event?: boolean;
  resourceType?: BookmarkTypes.InsightsResourceType;
  raw_cohort?: TODO;
  customPropertyId?: string | number;
  customProperty?: CustomProperty;
  joinPropertyType?: PropertyType;
  tempDataGroupId?: string;
  dataGroupId?: string;
  propertyObjectKey?: string | string[];
  scdEvent?: string;
  behavior?: Segmentation[`behavior`];
  scdOffset?: Behavior[`offset`];

  /** Used to specify a unit within a date property. For example, when segmenting on a date */
  unit?: BookmarkTypes.TimeUnit;

  listItemGroup?: Segmentation;
};

export type MetricMeasurement = BookmarkTypes.BehaviorMeasurement;

// New format which show clause will transform into
export type BehaviorMetric = BookmarkTypes.BehaviorShowClause;

/**
 * By default we abbreviate units like "10000" -> "10k"
 */
export const BEHAVIOR_METRIC_ABBREVIATION_DEFAULT = true;

export type BehaviorMetricDisplay = BookmarkTypes.MetricDisplay;

/**
 * FormulaMetric is used to represent 3 different types of formulas. Here's how to check for each type:
 * 1. inline formula: `id` is not set
 * 2. saved formula: only `id` is set
 * 3. saved formula with modifications: `name`, `definition`, `referencedMetrics` are set
 */
export type ShowClauseFormula = BookmarkTypes.FormulaShowClause;

export type FormulaReferencedMetrics = Array<BookmarkTypes.BehaviorShowClause>;

export type FormulaMeasurement = BookmarkTypes.FormulaMeasurement;

export type FormulaShowClause = BookmarkTypes.FormulaShowClause;

export type BehaviorShowClause = BookmarkTypes.BehaviorShowClause;
export type ShowClause = BookmarkTypes.BehaviorShowClause | BookmarkTypes.FormulaShowClause;

export interface GroupByCohort {
  dataset?: any;
  id?: number;
  name?: string;
  negated?: boolean;
  raw_cohort?: {
    unsavedId?: string;
    behaviors: SerializedBehaviorDictionary;
    selector: Expression;
    data_group_id?: string;
  };
}

export interface GroupClause {
  behavior?: Behavior;
  cohorts?: GroupByCohort[];
  customBucket?: Bucket;
  customGroups?: TODO;
  customProperty?: CustomProperty;
  customPropertyId?: number | string;
  dataGroupId?: string;
  dataset?: string;
  datasetId?: string;
  editing?: boolean | null;
  joinPropertyType?: PropertyType;
  listItemGroup?: GroupClause;
  listQuantifier?: ListQuantifier;
  profileType?: string;
  profileHistoryOptions?: ProfileHistoryOptions;
  projectId?: number;
  propertyObjectKey?: string | string[];
  propertyType?: PropertyType;
  resourceType: BookmarkTypes.InsightsResourceType;
  search?: string;
  startStepNumber?: number;
  endStepNumber?: number;
  // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  tempDataGroupId?: string;
  typeCast?: PropertyType;
  unit?: BookmarkTypes.TimeUnit;
  value?: string | string[] | null;
  scdEvent?: string;
  scdOptions?: Segmentation[`scdOptions`];
  isHidden?: boolean;
}

export interface FormulaClause {
  value: string;
  label?: string;
}

export interface FilterClause {
  dataset: string;
  value: string;
  resourceType: BookmarkTypes.InsightsResourceType;
  // it's always null
  profileType: null;
  search: string;
  dataGroupId: PropertyFilter[`dataGroupId`];
  // it is not the same shape as PropertyFilter['filterValue']
  filterValue: TODO;
  behavior?: PropertyFilter[`behavior`];
  filterType: PropertyFilter[`propertyType`];
  defaultType?: PropertyFilter[`propertyDefaultType`];
  filterOperator: PropertyFilter[`filterOperator`];
  propertyObjectKey?: PropertyFilter[`propertyObjectKey`];
  filterJoinType?: PropertyFilter[`joinPropertyType`];
  listQuantifier?: PropertyFilter[`listQuantifier`];
  listItemFilters?: PropertyFilter[`listItemFilters`];
  filterDateUnit?: PropertyFilter[`filterDateUnit`];
  customPropertyId?: PropertyFilter[`customPropertyId`];
  customProperty?: PropertyFilter[`customProperty`];
  isHidden?: PropertyFilter[`isHidden`];
  isInherited?: PropertyFilter[`isInherited`];
  profileHistoryOptions?: ProfileHistoryOptions;
  determiner?: FilterDeterminer;
  startStepNumber?: PropertyFilter[`startStepNumber`];
  endStepNumber?: PropertyFilter[`endStepNumber`];
  // when tempDataGroupId is set, it's pointing to the lookup table e.g. ['Distinct ID', 'Mapped Property']
  tempDataGroupId?: PropertyFilter[`tempDataGroupId`];
  scdEvent?: PropertyFilter[`scdEvent`];
  scdOptions?: PropertyFilter[`scdOptions`];
}

export interface InsightsBookmarkSections {
  show: ShowClause[];
  cohorts?: GroupByCohort[];
  group_by?: Segmentation[];
  group?: GroupClause[];
  filter?: FilterClause[];
  formula?: FormulaClause[];
  time: Array<BookmarkTypes.TimeClause>;
  globalDataGroupId?: string;
  metricLevelDataGroups?: boolean;
}

export function isInsightsBookmarkParams(params: any): params is BookmarkTypes.InsightsBookmarkParams {
  return params && params.sections && params.sections.show;
}
export type InsightsChartParams = Pick<
  BookmarkTypes.InsightsBookmarkParams,
  `sections` | `displayOptions` | `liftComparison`
>;

export type InsightsSeriesTree<ValueT = number> =
  | {[key: string]: ValueT}
  | {[key: string]: ValueT | InsightsSeriesTree<ValueT>};

interface DateRange {
  // sic; this is NOT BookmarkDateRange or a similar bookmark-related type
  from_date: string;
  to_date: string;
}

interface IncompleteBuckets {
  [date: string]: Array<null | string>;
}

export interface MetricMetadata {
  metric_key?: string;
  sub_queries?: Array<{metric_key: string}>;
  // funnels specific
  is_event_comparison?: boolean;
  // retention specific meta data
  retention_buckets_size?: number;
  incomplete_length?: number;
  incomplete_buckets?: IncompleteBuckets;
  past_incomplete_buckets?: IncompleteBuckets;
  /*
    Augmented on the client-side by queries/insights based on query limits and segments in the result
  */
  apiResultsTruncated?: boolean;
}

export interface ReportSectionsMetadata {
  cohorts?: Array<{bookmark: GroupByCohort}>;
  group?: Array<{bookmark: GroupClause; numeric_buckets?: Bucket}>;
  show?: MetricMetadata[];
  formula?: MetricMetadata[];
}

export type InsightsSegmentation = Omit<Segmentation, `resourceType`> & {
  resourceType: BookmarkTypes.InsightsResourceType;
};

export interface SegmentationQueryLimits {
  limit?: number;
}

export interface InsightsQueryError extends BaseQueryErrorType {
  formula_errors?: Array<{index: number; message: string}>;
}

export interface InsightsQueryResults<ValueT = number> extends BaseQueryResultsType {
  date_range: DateRange;
  headers: string[];
  meta: {
    min_sampling_factor: number;
    report_sections: ReportSectionsMetadata;
    /*
    Augmented on the client-side to carry the limits used on the query that generated these results.
    When limits affect the results, we need to tell the user where the cutoff is.
    */
    queryLimits?: SegmentationQueryLimits;
    apiResultsTruncated?: boolean;
    liftComparison?: any;
    time_period_settings?: {quarter_start_month: number};
  };
  series: InsightsSeriesTree<ValueT>;
  forecast_comparison?: {
    series: InsightsSeriesTree<ForecastValue>;

    // these properties are intended for manual debugging and shouldn't be referenced in non-experimental code, they will likely be removed after GA
    date_range: {
      historical_from_date: string;
      historical_to_date: string;
    };
    historical_series: InsightsSeriesTree;
  };
  secondary?: {
    series: InsightsSeriesTree<ValueT>;
  };
  secondary_time_comparison?: {
    series: InsightsSeriesTree<ValueT>;
  };
  time_comparison?: {
    date_range: DateRange;
    series: InsightsSeriesTree<ValueT>;
  };
  lift_comparison?: {
    series: InsightsSeriesTree<ValueT>;
  };
}

export interface AggregatedSeriesValue {
  // list of all possible paths to reach the segment in this series
  paths: string[][];

  // sum of all of this segments values in this series
  total: number;
}

export interface AggregatedSeries {
  [label: string]: AggregatedSeriesValue;
}
